import { APP_SHELL_TAB_ABSENCE, type AppShellRailButton } from '@efficiently/client-ui-app-shell';

/**
 * The id of the rail button every section hangs its navigation tree on — the Domains & Projects
 * navigator in Design and Supply, the domain tree in the Control Center. Each of those registers
 * content against this same id and only one of them holds it at a time.
 *
 * Named here because the table below is where the rail's ids are decided; the feature constants that
 * register against it mirror this value rather than the other way round.
 */
export const NAVIGATION_RAIL_BUTTON_ID = 'menu';

/** The shell's default panel width, `--eff-sys-layout-drawer-width`, in px. */
const DEFAULT_PANEL_WIDTH_PX = 336;

/** The AI Chat panel opens at 460px; its resize handle still narrows it to the default width. */
const AI_CHAT_PANEL_WIDTH_PX = 460;

/**
 * The two controls of the platform shell's left icon rail, in the order the rail renders them.
 *
 * They live beside the shell rather than inside `@efficiently/client-ui-app-shell` for exactly the
 * reason `APP_SECTIONS` lives outside it: that library is `type:ui` and knows no button by name
 * (TR-1). The table crosses the seam as DATA, handed over in the shell's constructor, and nothing
 * in the library imports this file.
 *
 * **Only `menu` carries an `absence` now**, and an `absence` is read only while NOTHING is registered
 * for the button. **Menu has content inside the Clarity section and nowhere else:**
 * `ClaritySectionHostComponent` registers the domain panel against this id when `/clarity` is
 * entered and takes it back on the way out, so the same button opens the tree in Control Center and
 * the placeholder in Design. That is why the entry keeps its `absence` rather than losing it — the
 * field describes the button where no feature has claimed it, not the button everywhere.
 *
 * **AI Chat lost its own** (EFF-26319, BR-9): `clarity-environment.routes.ts` registers the domain
 * chat panel against this id from an injector above BOTH sections, so the button opens the chat in
 * Design as well as in Control Center and there is no state left in which the placeholder should
 * explain it away. The one moment it renders nothing is the gap between the shell appearing and that
 * lazily loaded environment chunk resolving — a button pressed inside it opens an empty panel rather
 * than a message, which is the honest answer while the content is still on its way.
 *
 * `NOT_PORTED` says precisely that (as against `COMING_LATER`, which would claim the feature was
 * cut). Neither button is disabled: BR-5 is that a button whose feature has not landed still opens
 * the panel and explains itself, rather than refusing to act.
 *
 * Every icon comes from the generated sprite — `ai-chat` and `dehaze` are custom design-system glyphs
 * under `ui-icons/assets/icons/custom/`, and Menu swaps `dehaze` for the `closed-menu` cross while its
 * panel is open. `as const satisfies` is what
 * checks that rather than trusting it: the assertion narrows each `icon` to its literal, and the
 * `satisfies` fails to compile on a name absent from `EffIconName` instead of rendering an empty
 * glyph at runtime.
 *
 * Neither carries a `badge`. The rail renders one for a button that has it (BR-6), but nothing in
 * this boundary produces an unread count — the feature that knows AI Chat's will register it when
 * it lands.
 */
export const APP_RAIL_BUTTONS = [
	{
		id: 'ai-chat',
		label: 'AI Chat',
		panelTitle: 'Clarity AI',
		icon: 'ai-chat',
		panelScale: AI_CHAT_PANEL_WIDTH_PX / DEFAULT_PANEL_WIDTH_PX,
	},
	{
		id: NAVIGATION_RAIL_BUTTON_ID,
		label: 'Menu',
		icon: 'dehaze',
		expandedIcon: 'closed-menu',
		absence: APP_SHELL_TAB_ABSENCE.NOT_PORTED,
	},
] as const satisfies readonly AppShellRailButton[];
